'use strict';

import {Map as IMap, Set as ISet} from 'immutable';
import React, {useState, useEffect, useRef, createContext, useContext} from 'react';
import {getIn} from "../../shared/utils";
import TS from '@zerolight/zlthunder-time-utils';

const DashboardFilterContext = createContext(null);
const InitialFilter = updateFilter(new IMap(), {});

function updateFilter(filter, values) {
	return filter.withMutations(f => {
		for (let [k, v] of Object.entries(values)) {
			let tokens;
			if (!v) {
				f.delete(k);
				continue;
			}
			if (!Array.isArray(v)) {
				v = [v];
			}
			if (v[0] === '*') {
				f.delete(k);
				continue;
			}
			tokens = f.get(k) || new ISet();
			tokens = tokens.intersect(v).union(v);
			f.set(k, tokens);
		}
	});
}

function DashboardFilterProvider({children}) {
	const [filter, setFilter] = useState(InitialFilter);
	return (
		<DashboardFilterContext.Provider value={[filter, setFilter]}>
			{children}
		</DashboardFilterContext.Provider>
	);
}

function useDashboardFilter() {
	return useContext(DashboardFilterContext);
}

function isStale(data) {
	if (getIn(data, 'stale', false)) {
		return true;
	}
	let d = Symbol('default');
	let c = getIn(data, 'count', d);
	if (c !== d) {
		// It has "count" field, so it looks like summary, and probably not stale at this point
		return false;
	}

	if (Array.isArray(data)) {
		for (let d of data) {
			if (isStale(d)) {
				return true;
			}
		}
		return false;
	}

	if (data.constructor === Object) {
		for (let d of Object.values(data)) {
			if (isStale(d)) {
				return true;
			}
		}
		return false;
	}
	return false;
}

function useStaleDataLoader(loadData, dependencies = []) {
	const [stale, setStale] = useState(true);
	const cancelled = useRef(false);
	useEffect(() => {
		let _scheduled = null;
		cancelled.current = false;
		const _load = async () => {
			_scheduled = null;
			let s;
			try {
				s = await loadData(cancelled);
			} catch (err) {
				console.error('Unable to load data, will retry', err);
				setStale(true);
				_schedule(2 * TS.MINUTE);
				return;
			}

			if (cancelled.current) {
				return;
			}

			if (s) {
				setStale(true);
				_schedule(500);
			} else {
				setStale(false);
			}
		}

		const _schedule = (ts = 0) => {
			_scheduled = setTimeout(_load, ts);
		}
		_schedule();

		return () => {
			if (_scheduled) {
				clearTimeout(_scheduled);
			}
			cancelled.current = true;
		}
	}, [loadData, ...dependencies]);
	return stale;
}


export {DashboardFilterProvider, updateFilter, useDashboardFilter, useStaleDataLoader, isStale};